/**
 * Google Analytics 4.
 *
 * Loaded from JS rather than a <script> tag in index.html so it can be gated:
 * a static tag would fire on every `npm run dev` and pollute the property with
 * local traffic.
 *
 * The measurement ID is not a secret — it ships in the page source of every GA
 * site — so it lives here rather than in an env var.
 *
 * The hosts below must stay in step with the Content-Security-Policy in
 * infra/cloudfront.tf. A CSP that blocks them fails **silently**: no analytics,
 * no error anyone notices. src/test/analytics.test.ts asserts the two agree.
 */
export const GA_MEASUREMENT_ID = "G-WZXDECZW9V";

export const GA_SCRIPT_HOST = "https://www.googletagmanager.com";
export const GA_CONNECT_HOSTS = [
  "https://www.google-analytics.com",
  "https://*.google-analytics.com",
  "https://*.analytics.google.com",
  "https://www.googletagmanager.com",
] as const;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function optedOut(): boolean {
  // DNT is widely ignored these days, but honouring it costs nothing and the
  // analytics on a personal site are not worth overriding someone's stated
  // preference for.
  const dnt =
    navigator.doNotTrack ??
    (window as { doNotTrack?: string }).doNotTrack ??
    (navigator as { msDoNotTrack?: string }).msDoNotTrack;
  return dnt === "1" || dnt === "yes";
}

function shouldLoad(): boolean {
  if (!import.meta.env.PROD) return false;
  if (!GA_MEASUREMENT_ID) return false;
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (optedOut()) return false;
  // Already injected — StrictMode double-invokes effects in development, and a
  // second gtag.js would double-count every page view.
  if (window.dataLayer) return false;
  return true;
}

export function initAnalytics(locale?: string): void {
  if (!shouldLoad()) return;

  window.dataLayer = window.dataLayer || [];
  // Deliberately the canonical snippet: a non-arrow function pushing its own
  // `arguments` object. gtag.js reads that object directly, so a rest
  // parameter spread into an array is not a drop-in substitute.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag;

  // Consent Mode v2. This MUST be the first thing on the dataLayer — gtag.js
  // applies whatever state it finds when it initialises, so a default queued
  // after `config` would arrive too late and the first hit would be treated as
  // consented.
  //
  // Denied `analytics_storage` means no cookie, no client id, no user
  // identifier. gtag.js still sends a cookieless ping, which is what makes the
  // tag detectable; it carries no identifier and cannot be joined to a person.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    // Hold hits briefly so a fast "Accept" is reflected in the first page view
    // rather than arriving as a second, consented hit.
    wait_for_update: 500,
  });

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    // Which language the site actually rendered in, as opposed to the browser
    // language GA collects on its own — the whole point of the pt-BR work.
    ...(locale ? { site_language: locale } : {}),
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `${GA_SCRIPT_HOST}/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/**
 * Flips `analytics_storage` once the visitor answers the banner. Only this
 * grant lets GA set a cookie or persist a client id.
 *
 * No-ops when analytics never loaded (development, or Do Not Track).
 */
export function setAnalyticsConsent(granted: boolean): void {
  window.gtag?.("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
  });
}

/** No-ops when analytics never loaded, so callers need no guard. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  window.gtag?.("event", name, params);
}
